import { observeArray, observeProperties, PropertyObserver } from "../../Observer";
import { Subscription } from "../../Signal";
import { compile, replace } from "../Compiler";
import { context as globalContext, TemplateContext } from "../Template";
import { Expression } from "./Expression";

/**
 * Super class of expression that will render something into to dom
 */
export abstract class RenderExpression extends Expression {


	root:ObjectSubscriber;


	constructor( context:TemplateContext, expression:string ) {
		super( context );
		this.expression = compile( expression, [ 'data' ] );

		// disable logging in trace phase
		const prevLogging = globalContext.logging;
		globalContext.logging = false;
		this.trace();
		globalContext.logging = prevLogging;

		this.subscribe();
	}


	/**
	 * updates the dom when the data changes
	 */
	abstract update();


	/**
	 * The inital render call is called to render the first time
	 */
	inital() {
		this.update();
	}

	/**
	 * Implements subscribe to listen on changes of context data
	 */
	subscribe() {
		this.root.subscribeTo( this.context.data );
	}



	destroy() {
		this.root.destroy();
	}


	/**
	 * Traces the expression to collect property access
	 */
	trace() {

		this.root = new ObjectSubscriber( this );

		var trapper = {
			has: () => {
				return true;
			},
			get:( t, key, p ) => {

				if( key == Symbol.unscopables ) return {};
				if( key == Symbol.toPrimitive ) return ( hint ) => {
					if( hint == 'number' ) return 1;
					return '';
				};

				// check blacklisted global vars
				// *this* is the access on the this context in IE Edge Legacy
				if( [ 'JSON', 'Math', '*this*' ].indexOf( key ) >= 0 ) return;

				// handle data access
				if( key == 'data' && t == this.root ) return t;


				// create objectobserver for property if there is not already one.
				if( !t.properties.has( key ) ) {
					t.properties.set( key, new ObjectSubscriber( t.expression ) );
				}

				return new Proxy( t.properties.get( key ), trapper );
			},
		}

		this.expression.call( this.context, new Proxy( this.root, trapper ) );
	}


	/**
	 * Helper function that returns the value of the expression
	 */
	getExpressionValue() {
		return this.expression.call( this.context, this.context.data );
	}
}


const booleanAttributes = {
	'checked': [
		HTMLInputElement,
	],
	'disabled': [
		HTMLButtonElement,
		HTMLFieldSetElement,
		HTMLInputElement,
		HTMLOptGroupElement,
		HTMLOptionElement,
		HTMLSelectElement,
		HTMLTextAreaElement,
	],
	'selected': [
		HTMLOptionElement,
	],
	'hidden': [
		Element,
	],
	'readonly': [
		HTMLInputElement,
		HTMLTextAreaElement,
	],
	'required': [
		HTMLInputElement,
		HTMLTextAreaElement,
		HTMLSelectElement,
	],
};

/**
 * This class monitors expression on node attributes like: <div name="[[ my expression ]]"></div>
 */
export class AttributeExpression extends RenderExpression {

	node:Element;
	attribute:Attr;
	boolean:boolean;

	constructor( node:Element, attribute:Attr, context:TemplateContext ) {
		super( context, attribute.value );
		this.node = node;
		this.attribute = attribute;
		this.boolean = booleanAttributes[ attribute.name ]?.some( elementType => node instanceof elementType );
	}

	update() {
		// handle input value
        if( this.node instanceof HTMLInputElement || this.node instanceof HTMLTextAreaElement ) {
            if( this.attribute.name == 'value' || this.attribute.name == 'checked' ) {
                this.node[ this.attribute.name ] = this.getExpressionValue();
            }
        }

		if( this.boolean ) {
			if( this.getExpressionValue() ) {
				this.node.setAttribute( this.attribute.name, 'true' );
			} else {
				this.node.removeAttribute( this.attribute.name );
			}
		}
		else {
			this.node.setAttribute( this.attribute.name, this.getExpressionValue() );
		}
	}
}


/**
 * This class monitors expression on node class attibute like: <div class="[[ my expression ]]"></div>
 * It supports the object syntax for classes: [[ { 'demo':true } ]]
 */
export class CSSExpression extends AttributeExpression {

	expressionString:string;

	constructor( node:Element, attribute:Attr, context:TemplateContext ) {
		super( node, attribute, context );
		this.expressionString = attribute.value;
	}

	getExpressionValue() {
		// replace all parts of the template for exp. "static [[ exp1 ]] demo [[ exp2 ]]"
		return replace( this.expressionString, ( match ) => {
			let value = compile( match ).call( this.context, this.context.data );

			// if its an object handle
			if( value && typeof value == 'object' ) return Object.keys( value ).filter( key => value[key] ).join( ' ' );

			return value;
		});
	}
}



/**
 * This class monitors expression on text nodes like: <div>[[ my expression ]]</div>
 */
export class TextExpression extends RenderExpression {

	node:Node;

	constructor( node:Node, context:TemplateContext ) {
		super( context, node.nodeValue );
		this.node = node;
	}

	update() {
		this.node.nodeValue = this.context.empty( this.getExpressionValue() );
	}
}



/**
 * A helper class to manage subscriptions on objects with a given set of properties
 */
class ObjectSubscriber {

	subscription:Subscription;
	properties:Map<string,ObjectSubscriber>;
	expression:RenderExpression;

	constructor( exp:RenderExpression ) {
		this.properties = new Map();
		this.expression = exp;
	}


	subscribeTo( obj:any ) {

		this.unsubscribe();

		// do not subscribe on undefined
		if( !obj ) return;

		// do not subscribe on anythinf else than object
		if( !( obj instanceof Object) ) return;

		// check if we have properties to listen
		if( !this.hasProperties() ) return;

		// subscribe on arrays
		if( Array.isArray( obj ) ) {
			this.subscription = observeArray( obj ).arrayChange.subscribe( this.onArrayChange.bind( this ) );

		// subscribe to object
		} else {
			// observe properties of expression in given obj
			let props =[ ...this.properties.keys() ];
			props.unshift( obj );
			let obs = observeProperties.apply( null, props ) as PropertyObserver;

			// subscribe to changes on obj
			this.subscription = obs.propertyChange.subscribe( this.onObjectChange.bind( this ) );
		}


		// subscribe child properties
		this.properties.forEach( ( value, key ) => {
			value.subscribeTo( obj[ key ] );
		});
	}


	unsubscribe() {
		if( this.subscription ) this.subscription.unsubscribe();
		this.properties.forEach( ( value, key ) => {
			value.unsubscribe();
		});
	}


	onObjectChange( e:{ name:string } ) {

		// check if this expression has the changed property
		let obj = this.properties.get( e.name );
		if( obj ) {

			// update expression content
			this.expression.update();

			// if the changed propery has sub properties we need to resubscribe
			if( obj.hasProperties() ) this.expression.subscribe();
		}
	}

	onArrayChange( e ) {
		// update expression content
		this.expression.update();

		// resubscribe if there is an access on array indices/properties = 0,1,...
		if( this.properties.size > 0 ) {
			this.expression.subscribe();
		}
	}


	hasProperties() {
		return this.properties.size > 0;
	}

	destroy() {

		this.unsubscribe();

		for (const child of this.properties.values() ) {
			child.destroy();
		}

		this.properties = new Map();
	}
}
